import axiosInstance from "./axiosInstance";

export interface AdvanceReceiptApi {
  receiptId: number;
  receiptNo: string;
  receiptDate: string;
  tourOperatorId: number;
  tourOperatorName: string;
  officeName: string;
  itsNumber: string;
  currency: string;
  amount: number; 
  paymentMode: string;
  transactionRef: string;
  remarks: string;
  description: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  status: "DRAFT" | "POSTED";
  createdByUserName: string;
}

export interface AdvanceReceiptListResponse {
  items: AdvanceReceiptApi[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
}

export interface AdvanceReceiptListQuery {
  searchText?: string | null;
  receiptNo?: string | null;
  tourOperatorId?: number | null;
  paymentMode?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  pageNo?: number;
  pageSize?: number;

  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface CreateAdvanceReceiptPayload {
  receiptDate: string;
  tourOperatorId: number;
  currency: string;
  amount: number;
  paymentMode: string;
  transactionRef?: string | null;
  remarks?: string | null;
  description?: string | null;
  createdBy: number;
}

export interface AdvanceReceiptDetailApi {
  receiptId: number;
  receiptNo: string;
  receiptDate: string;
  tourOperatorId: number;
  tourOperatorName: string;
  createdByUserName: string;
  amount: number;
  paymentMode: string;
  transactionRef?: string | null;
  remarks?: string | null;
  description?: string | null;
  status?: "DRAFT" | "POSTED";
}

export interface UpdateAdvanceReceiptPayload {
  receiptId: number;
  amount: number;
  receiptDate: string;
  paymentMode: string;
  transactionRef?: string | null;
  remarks?: string | null;
  description?: string | null;
}

export interface TourOperatorApi {
  tourOperatorId: number;
  itsNumber: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  officeName: string;
  mobileNumber: string;
  businessType: string;
  countryId: number;
  cityId: string;
  status: boolean | null;
}

export interface AdvanceReceiptSummaryApi {
  totalReceipts: number;
  totalAmount: number;
  totalDraftReceipts: number;
  totalDraftAmount: number;
  cash: number;
  cheque: number;
  bankTransfer: number;
  upi: number;
  card: number;
  totalUsedPaymentMethods: number;
}

export interface BaseCurrencyApi {
  currencyId: number;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  exchangeRate: number;
  isBaseCurrency: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
} 

export const getAuthHeader = () => { 
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getAdvanceReceipts = async (
  params: AdvanceReceiptListQuery
): Promise<AdvanceReceiptListResponse> => {
  const res = await axiosInstance.get("/advance-receipts/list", { 
    params,
    headers: getAuthHeader(),
  });
  return res.data;
};

export const createAdvanceReceipt = async (
  payload: CreateAdvanceReceiptPayload
) => {
  const res = await axiosInstance.post("/advance-receipts", payload, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const postAdvanceReceipt = async (receiptId: number) => {
  const res = await axiosInstance.post(`/advance-receipts/${receiptId}/post`,{
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getAdvanceReceiptById = async (
  receiptId: number
): Promise<AdvanceReceiptDetailApi> => {
  const res = await axiosInstance.get(`/advance-receipts/${receiptId}`,{
    headers: getAuthHeader(),
  });
  return res.data;
};

export const updateAdvanceReceipt = async (
  payload: UpdateAdvanceReceiptPayload
) => {
  const { receiptId, ...body } = payload;
  const res = await axiosInstance.put(`/advance-receipts/${receiptId}`, body,{
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getTourOperators = async (): Promise<TourOperatorApi[]> => {
  const res = await axiosInstance.get("/tour-operator", {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getAdvanceReceiptSummary =
  async (): Promise<AdvanceReceiptSummaryApi> => {
    const res = await axiosInstance.get("/advance-receipts/summary", {
      headers: getAuthHeader(),
    });
    return res.data;
  };

export const downloadAdvanceReceiptPdf = async (
  receiptId: number
): Promise<Blob> => {
  const res = await axiosInstance.get(`/advance-receipts/${receiptId}/pdf`, {
    responseType: "blob",
    headers: getAuthHeader(),
  });

  return res.data;
}; 

export const getBaseCurrency = async (): Promise<BaseCurrencyApi> => {
  const { data } = await axiosInstance.get("/Currency/base-currency", {
    headers: getAuthHeader(),
  });
  return data;
};

export const getApprovedTourOperators = async (): Promise<TourOperatorApi[]> => {
  const res = await axiosInstance.get("/tour-operator/approved", {
    headers: getAuthHeader(),
  });
  return res.data;
};